import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Badge, Btn, dt } from "../components/common";
import { useAuth } from "../auth";
import { Plus, X, Trash2, MessageSquare, Send, AlertTriangle, UserCheck, Paperclip, List, Columns3, Eye } from "lucide-react";
import { toast } from "sonner";
import WysiwygEditor from "../components/WysiwygEditor";
import AccountPicker from "../components/AccountPicker";
import TicketDrawer from "../components/TicketDrawer";
import { useAutoRefresh } from "../lib/autoRefresh";

const NEXT = {
  aberto: ["em_curso", "fechado"],
  em_curso: ["a_aguardar_cliente", "resolvido"],
  a_aguardar_cliente: ["em_curso", "resolvido"],
  resolvido: ["fechado", "em_curso"],
};

export default function Tickets() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null); // {organizacao_nome, assunto, descricao}
  const [toDelete, setToDelete] = useState(null);
  const [detail, setDetail] = useState(null);
  const [drawerTicket, setDrawerTicket] = useState(null);

  const load = () => api.get("/tickets").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  // Suporta abertura directa a partir do drawer: ?nova=1&organizacao_nome=&assunto=
  useEffect(() => {
    if (params.get("nova") === "1") {
      setPrefill({
        organizacao_nome: params.get("organizacao_nome") || "",
        assunto: params.get("assunto") || "",
        descricao: params.get("descricao") || "",
      });
      setOpen(true);
      const next = new URLSearchParams(params);
      ["nova", "organizacao_nome", "assunto", "descricao", "account_id"].forEach((k) => next.delete(k));
      setParams(next, { replace: true });
    }
  }, []); // eslint-disable-line

  const transition = async (id, estado) => {
    try {
      await api.post(`/tickets/${id}/transition`, { estado });
      toast.success(`Ticket -> ${estado.replace(/_/g, " ")}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/tickets/${toDelete.id}`);
      toast.success("Ticket eliminado");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const canDelete = can("*");
  const [soSla, setSoSla] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem("tickets.view") || "list");
  useEffect(() => { localStorage.setItem("tickets.view", view); }, [view]);
  const slaCount = rows.filter((r) => r.sla_excedido).length;
  const visibleRows = soSla ? rows.filter((r) => r.sla_excedido) : rows;

  const cols = [
    { key: "referencia", label: "Ref.", render: (r) => <code className="text-xs bg-tint px-2 py-0.5 rounded">{r.referencia || r.hubspot_ticket_id || "-"}</code> },
    { key: "assunto", label: "Assunto", render: (r) => (
      <button onClick={(e) => { e.stopPropagation(); setDrawerTicket(r); }}
        data-testid={`ticket-drawer-open-${r.id}`}
        className="font-semibold text-navy text-left hover:text-blue hover:underline">{r.assunto}</button>
    ) },
    { key: "organizacao_nome", label: "Cliente" },
    { key: "prioridade", label: "Prioridade", render: (r) => <Badge value={r.prioridade} /> },
    { key: "origem", label: "Origem" },
    { key: "estado", label: "Estado", render: (r) => (
      <div className="flex items-center gap-1.5">
        <Badge value={r.estado} />
        {r.sla_excedido && <span data-testid={`sla-badge-${r.id}`} className="text-[10px] font-bold bg-red-100 text-red-700 rounded-full px-2 py-0.5 inline-flex items-center gap-1"><AlertTriangle size={10} /> SLA excedido</span>}
        {!r.sla_excedido && !r.sla_respondido && r.sla_restante_horas != null && r.sla_restante_horas >= 0 && <span className="text-[10px] text-muted2">resta {r.sla_restante_horas}h</span>}
      </div>
    ) },
    { key: "criado_em", label: "Criado", render: (r) => dt(r.criado_em) },
    {
      key: "acoes",
      label: "Acoes",
      render: (r) => (
        <div className="flex gap-1 flex-wrap items-center">
          <button onClick={() => setDrawerTicket(r)} data-testid={`ticket-drawer-btn-${r.id}`}
            title="Ver detalhes"
            className="text-xs p-1.5 rounded bg-slate-100 text-slate-700 hover:bg-navy hover:text-white transition-colors"><Eye size={13} /></button>
          <button onClick={() => setDetail(r)} data-testid={`ticket-open-${r.id}`}
            className="text-xs px-2 py-1 rounded bg-blue/10 text-blue hover:bg-blue hover:text-white transition-colors inline-flex items-center gap-1"><MessageSquare size={13} /> Responder</button>
          {(NEXT[r.estado] || []).map((s) => (
            <button key={s} onClick={() => transition(r.id, s)} data-testid={`ticket-${r.id}-${s}`}
              className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white transition-colors">{s.replace(/_/g, " ")}</button>
          ))}
          {canDelete && (
            <button onClick={() => setToDelete(r)} data-testid={`ticket-delete-${r.id}`} title="Eliminar ticket"
              className="text-xs p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        overline="Service Hub"
        title="Suporte"
        action={
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-slate-100 rounded-md p-0.5" data-testid="tickets-view-switch">
              <button onClick={() => setView("list")} data-testid="tickets-view-list"
                className={`px-2.5 py-1 rounded text-[12px] font-medium inline-flex items-center gap-1 ${view === "list" ? "bg-white text-navy shadow-sm" : "text-slate-600 hover:text-navy"}`}>
                <List size={13} /> Lista
              </button>
              <button onClick={() => setView("kanban")} data-testid="tickets-view-kanban"
                className={`px-2.5 py-1 rounded text-[12px] font-medium inline-flex items-center gap-1 ${view === "kanban" ? "bg-white text-navy shadow-sm" : "text-slate-600 hover:text-navy"}`}>
                <Columns3 size={13} /> Kanban
              </button>
            </div>
            {can("tickets:create") && <Btn onClick={() => setOpen(true)} testid="new-ticket-btn"><Plus size={16} /> Novo ticket</Btn>}
          </div>
        }
      />
      {slaCount > 0 && (
        <button onClick={() => setSoSla((v) => !v)} data-testid="sla-filter-toggle"
          className={`mb-3 inline-flex items-center gap-2 text-sm font-semibold rounded-lg px-3 py-1.5 transition-colors ${soSla ? "bg-red-600 text-white" : "bg-red-50 text-red-700 hover:bg-red-100"}`}>
          <AlertTriangle size={15} /> {slaCount} ticket(s) com SLA excedido{soSla ? " (a mostrar so estes)" : ""}
        </button>
      )}
      {view === "list" && <Table columns={cols} rows={visibleRows} testid="tickets-table" />}
      {view === "kanban" && <TicketsKanban rows={visibleRows} onTransition={transition} onOpen={setDrawerTicket} />}
      {open && <TicketModal onClose={() => { setOpen(false); setPrefill(null); }} onSaved={() => { setOpen(false); setPrefill(null); load(); }} initial={prefill} />}
      {detail && <TicketDetail ticket={detail} onClose={() => setDetail(null)} onReplied={() => { load(); }} />}
      {drawerTicket && (
        <TicketDrawer
          ticket={drawerTicket}
          open={!!drawerTicket}
          onClose={() => setDrawerTicket(null)}
          onTransition={(id, s) => { transition(id, s); setDrawerTicket(null); }}
          onOpenFull={(t) => { setDrawerTicket(null); setDetail(t); }}
        />
      )}
      {toDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="ticket-delete-modal">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <h2 className="text-navy text-xl font-bold tracking-tight mb-2">Eliminar ticket</h2>
            <p className="text-muted2 text-sm mb-6">
              Vai eliminar permanentemente o ticket <b className="text-navy">{toDelete.assunto}</b> ({toDelete.organizacao_nome}). Esta acao nao pode ser desfeita.
            </p>
            <div className="flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setToDelete(null)} testid="ticket-delete-cancel">Cancelar</Btn>
              <Btn variant="danger" onClick={doDelete} testid="ticket-delete-confirm">Eliminar</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TicketModal({ onClose, onSaved, initial }) {
  const [form, setForm] = useState({
    organizacao_nome: initial?.organizacao_nome || "",
    assunto: initial?.assunto || "",
    descricao: initial?.descricao || "",
    prioridade: "media",
    origem: "interno",
  });
  const [preview, setPreview] = useState(null); // {matched, rule_nome, owner_email, owner_nome, ...}
  const upd = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => {
    const t = setTimeout(() => {
      const ready = form.organizacao_nome || form.assunto || form.descricao;
      if (!ready) { setPreview(null); return; }
      api.post("/tickets/preview-rule", form).then(({ data }) => setPreview(data)).catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(t);
  }, [form]);

  const save = async () => {
    if (!form.assunto || !form.organizacao_nome) return toast.error("Preencha cliente e assunto");
    try {
      await api.post("/tickets", form);
      toast.success("Ticket criado");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="ticket-modal">
      <div className="bg-white rounded-xl p-8 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-navy text-xl font-bold tracking-tight">Novo ticket</h2>
          <button onClick={onClose}><X className="text-muted2" /></button>
        </div>
        <label className="block text-sm font-semibold text-navy mb-1">Cliente</label>
        <AccountPicker
          value={form.organizacao_nome}
          onSelect={(r) => upd("organizacao_nome", r.nome)}
          onChangeText={(v) => upd("organizacao_nome", v)}
          testid="ticket-org"
          className="mb-3"
          placeholder="Escreva para pesquisar contas..."
        />
        <label className="block text-sm font-semibold text-navy mb-1">Assunto</label>
        <input value={form.assunto} onChange={(e) => upd("assunto", e.target.value)} data-testid="ticket-subject" className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" />
        <label className="block text-sm font-semibold text-navy mb-1">Descricao</label>
        <textarea value={form.descricao} onChange={(e) => upd("descricao", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg" rows={3} />
        <label className="block text-sm font-semibold text-navy mb-1">Prioridade</label>
        <select value={form.prioridade} onChange={(e) => upd("prioridade", e.target.value)} className="w-full mb-3 px-3 py-2 border border-tint rounded-lg">
          <option value="baixa">Baixa</option>
          <option value="media">Media</option>
          <option value="alta">Alta</option>
        </select>
        {preview && preview.matched && (
          <div data-testid="ticket-rule-preview" className="mb-4 flex items-start gap-2 rounded-lg border border-blue/30 bg-blue/5 p-3 text-sm">
            <UserCheck className="text-blue mt-0.5 shrink-0" size={16} />
            <div className="flex-1">
              <div className="text-navy font-semibold">Sera atribuido a {preview.owner_nome || preview.owner_email}</div>
              <div className="text-[11px] text-muted2">Regra "{preview.rule_nome}" - prioridade {preview.prioridade_ordem}{!preview.owner_existe ? " - AVISO: utilizador nao encontrado, ficara com o autor" : ""}</div>
            </div>
          </div>
        )}
        {preview && !preview.matched && (form.organizacao_nome || form.assunto) && (
          <div data-testid="ticket-rule-preview" className="mb-4 flex items-start gap-2 rounded-lg border border-tint bg-tint/40 p-3 text-xs text-muted2">
            <UserCheck size={14} className="mt-0.5 shrink-0" />
            <span>Sem regra correspondente. O ticket ficara atribuido ao autor.</span>
          </div>
        )}
        <Btn onClick={save} testid="ticket-save">Criar ticket</Btn>
      </div>
    </div>
  );
}

function TicketDetail({ ticket, onClose, onReplied }) {
  const [msgs, setMsgs] = useState(ticket.mensagens || []);
  const [templates, setTemplates] = useState([]);
  const [kb, setKb] = useState([]);
  const [kbQuery, setKbQuery] = useState("");
  const [kbCat, setKbCat] = useState("");
  const [html, setHtml] = useState("");
  const [para, setPara] = useState("");
  const [enviarEmail, setEnviarEmail] = useState(true);
  const [busy, setBusy] = useState(false);
  const [anexos, setAnexos] = useState([]); // [{articleId, anexo:{id,nome,mime,tamanho}, content_b64}]

  useEffect(() => {
    api.get("/crm/email-templates?contexto=servicedesk").then(({ data }) => setTemplates(data || [])).catch(() => {});
    api.get("/kb/articles").then(({ data }) => setKb(data || [])).catch(() => {});
  }, []);

  const relevantes = templates.filter((t) => !t.estado_alvo || t.estado_alvo === ticket.estado);
  const applyTemplate = (chave) => {
    const t = templates.find((x) => x.chave === chave);
    if (t) setHtml(t.html || "");
  };
  const inserirArtigo = (aid) => {
    const art = kb.find((x) => x.id === aid);
    if (art) setHtml((h) => (h || "") + (art.conteudo || ""));
  };
  const anexarPdf = async (articleId, anexo) => {
    if (anexos.some((x) => x.anexo.id === anexo.id)) { toast.message("Este anexo ja esta na resposta."); return; }
    try {
      const { data } = await api.get(`/kb/articles/${articleId}/anexos/${anexo.id}`);
      setAnexos((a) => [...a, { articleId, anexo: { id: anexo.id, nome: anexo.nome, mime: anexo.mime, tamanho: anexo.tamanho }, content_b64: data.content_b64 }]);
      toast.success(`"${anexo.nome}" adicionado a resposta.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeAnexo = (aid) => setAnexos((a) => a.filter((x) => x.anexo.id !== aid));

  const categorias = Array.from(new Set(kb.map((a) => (a.categoria || "geral")))).sort();
  const kbFiltrada = kb.filter((a) => {
    if (kbCat && (a.categoria || "geral") !== kbCat) return false;
    if (kbQuery.trim()) {
      const q = kbQuery.toLowerCase();
      return (a.titulo || "").toLowerCase().includes(q) || (a.conteudo || "").toLowerCase().includes(q);
    }
    return true;
  });

  const send = async () => {
    if (!html.trim()) return toast.error("Escreva a resposta.");
    setBusy(true);
    try {
      const attachments = anexos.map((x) => ({ filename: x.anexo.nome, content: x.content_b64, type: x.anexo.mime || "application/octet-stream" }));
      const { data } = await api.post(`/tickets/${ticket.id}/reply`, { mensagem_html: html, para, enviar_email: enviarEmail, attachments });
      setMsgs((m) => [...m, { autor: "eu", tipo: "agente", mensagem: html, em: new Date().toISOString() }]);
      setHtml(""); setAnexos([]);
      if (enviarEmail) {
        if (data.email_estado === "enviado") toast.success(`Resposta enviada para ${data.para}.`);
        else if (data.email_estado === "nao_configurado") toast.message("Resposta guardada. SendGrid nao configurado - email nao enviado.");
        else toast.message(`Resposta guardada. Estado do email: ${data.email_estado}`);
      } else toast.success("Resposta guardada no ticket.");
      onReplied();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="ticket-detail-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start p-6 border-b border-tint">
          <div>
            <div className="flex items-center gap-2"><h2 className="text-navy text-xl font-bold tracking-tight">{ticket.assunto}</h2><Badge value={ticket.estado} /></div>
            <p className="text-muted2 text-sm mt-1">{ticket.organizacao_nome} - prioridade {ticket.prioridade}</p>
          </div>
          <button onClick={onClose} data-testid="ticket-detail-close"><X className="text-muted2" /></button>
        </div>
        <div className="p-6 space-y-4">
          {ticket.descricao && <div className="bg-tint/50 rounded-lg p-3 text-sm text-navy"><span className="text-xs text-muted2 block mb-1">Descricao</span>{ticket.descricao}</div>}
          <div className="space-y-2" data-testid="ticket-thread">
            {msgs.length === 0 && <p className="text-sm text-muted2">Sem mensagens ainda.</p>}
            {msgs.map((m, i) => (
              <div key={i} className={`rounded-lg p-3 text-sm ${m.tipo === "agente" ? "bg-blue/10 ml-6" : "bg-tint mr-6"}`}>
                <div className="text-[11px] text-muted2 mb-1">{m.tipo === "agente" ? "Agente" : "Cliente"} - {m.autor} - {dt(m.em)}</div>
                <div className="text-navy" dangerouslySetInnerHTML={{ __html: m.mensagem || "" }} />
              </div>
            ))}
          </div>
          <div className="border-t border-tint pt-4 space-y-3">
            <h3 className="text-navy font-semibold text-sm">Responder</h3>
            {relevantes.length > 0 && (
              <div>
                <label className="block text-sm font-semibold text-navy mb-1">Template (sugeridos para "{ticket.estado.replace(/_/g, " ")}")</label>
                <select onChange={(e) => e.target.value && applyTemplate(e.target.value)} data-testid="ticket-template" className="w-full px-3 py-2 border border-tint rounded-lg text-sm bg-white">
                  <option value="">-- Escolher template --</option>
                  {relevantes.map((t) => <option key={t.chave} value={t.chave}>{t.nome || t.chave}</option>)}
                </select>
              </div>
            )}
            {kb.length > 0 && (
              <div className="bg-tint/40 rounded-lg p-3 space-y-2" data-testid="kb-panel">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-semibold text-navy">Base de conhecimento</label>
                  <span className="text-[11px] text-muted2">{kbFiltrada.length}/{kb.length} artigos</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input value={kbQuery} onChange={(e) => setKbQuery(e.target.value)} placeholder="Pesquisar por titulo ou texto..." data-testid="kb-search" className="px-3 py-2 border border-tint rounded-lg text-sm bg-white" />
                  <select value={kbCat} onChange={(e) => setKbCat(e.target.value)} data-testid="kb-category-filter" className="px-3 py-2 border border-tint rounded-lg text-sm bg-white">
                    <option value="">Todas as categorias</option>
                    {categorias.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1 border border-tint rounded bg-white">
                  {kbFiltrada.length === 0 && <p className="text-xs text-muted2 p-2">Sem artigos correspondentes.</p>}
                  {categorias.map((cat) => {
                    const artigos = kbFiltrada.filter((a) => (a.categoria || "geral") === cat);
                    if (artigos.length === 0) return null;
                    return (
                      <div key={cat} data-testid={`kb-cat-${cat}`}>
                        <div className="text-[10px] uppercase tracking-wider text-muted2 font-semibold px-2 pt-2">{cat}</div>
                        {artigos.map((a) => (
                          <div key={a.id} className="border-t border-tint first:border-t-0">
                            <button onClick={() => inserirArtigo(a.id)} data-testid={`kb-insert-${a.id}`}
                              className="w-full text-left px-2 py-1.5 text-sm text-navy hover:bg-blue/10 hover:text-blue transition-colors flex items-center justify-between gap-2">
                              <span className="truncate">{a.titulo}</span>
                              <span className="text-[10px] text-muted2 shrink-0">inserir texto +</span>
                            </button>
                            {(a.anexos || []).map((anx) => (
                              <button key={anx.id} onClick={() => anexarPdf(a.id, anx)} data-testid={`kb-attach-${anx.id}`}
                                className="w-full text-left pl-6 pr-2 py-1 text-[11px] text-muted2 hover:bg-blue/5 hover:text-blue transition-colors flex items-center gap-1.5">
                                <Paperclip size={11} /> <span className="truncate flex-1">{anx.nome}</span>
                                <span className="text-[10px] shrink-0">{Math.round((anx.tamanho || 0) / 1024)} KB - anexar</span>
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {anexos.length > 0 && (
              <div className="border border-tint rounded-lg p-2 space-y-1" data-testid="ticket-attachments">
                <div className="text-[11px] uppercase tracking-wider text-muted2 font-semibold">Anexos ({anexos.length})</div>
                {anexos.map((x) => (
                  <div key={x.anexo.id} className="flex items-center gap-2 text-sm text-navy bg-tint/50 rounded px-2 py-1">
                    <Paperclip size={13} className="text-blue shrink-0" />
                    <span className="truncate flex-1">{x.anexo.nome}</span>
                    <span className="text-[11px] text-muted2 shrink-0">{Math.round((x.anexo.tamanho || 0) / 1024)} KB</span>
                    <button onClick={() => removeAnexo(x.anexo.id)} data-testid={`ticket-attach-remove-${x.anexo.id}`} className="text-red-600 hover:text-red-800"><X size={13} /></button>
                  </div>
                ))}
              </div>
            )}
            <WysiwygEditor value={html} onChange={setHtml} minHeight={160} />
            <input value={para} onChange={(e) => setPara(e.target.value)} placeholder="Email do cliente (vazio = contacto da conta)" data-testid="ticket-reply-para" className="w-full px-3 py-2 border border-tint rounded-lg text-sm" />
            <label className="flex items-center gap-2 text-sm text-navy">
              <input type="checkbox" checked={enviarEmail} onChange={(e) => setEnviarEmail(e.target.checked)} data-testid="ticket-reply-email-toggle" /> Enviar por email ao cliente
            </label>
            <div className="flex justify-end"><Btn onClick={send} testid="ticket-reply-send" disabled={busy}><Send size={15} /> {busy ? "A enviar..." : "Responder"}</Btn></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- KANBAN ----------
const KANBAN_COLS = [
  { key: "aberto", label: "Aberto" },
  { key: "em_curso", label: "Em curso" },
  { key: "a_aguardar_cliente", label: "A aguardar cliente" },
  { key: "resolvido", label: "Resolvido" },
];

const PRIO_BORDER = { alta: "border-l-red-500", media: "border-l-amber-500", baixa: "border-l-slate-300" };

function TicketsKanban({ rows, onTransition, onOpen }) {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const byCol = KANBAN_COLS.reduce((acc, c) => ({ ...acc, [c.key]: rows.filter((r) => r.estado === c.key) }), {});
  const drop = (col) => {
    if (drag) {
      const row = rows.find((r) => r.id === drag);
      if (row && row.estado !== col) onTransition(drag, col);
    }
    setDrag(null); setOver(null);
  };
  return (
    <div className="grid gap-3 overflow-x-auto pb-2" data-testid="tickets-kanban"
         style={{ gridTemplateColumns: `repeat(${KANBAN_COLS.length}, minmax(260px, 1fr))` }}>
      {KANBAN_COLS.map((c) => (
        <div key={c.key}
             onDragOver={(e) => { e.preventDefault(); setOver(c.key); }}
             onDragLeave={() => setOver((v) => (v === c.key ? null : v))}
             onDrop={() => drop(c.key)}
             data-testid={`kanban-col-${c.key}`}
             className={`rounded-lg border transition-colors flex flex-col min-h-[420px] ${over === c.key ? "border-blue bg-blue-50/40" : "border-slate-200 bg-slate-50/40"}`}>
          <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-inherit rounded-t-lg">
            <div className="flex items-center gap-2">
              <Badge value={c.key} />
              <span className="text-[11px] text-slate-500 font-medium">{byCol[c.key].length}</span>
            </div>
          </div>
          <div className="p-2 space-y-2 flex-1">
            {byCol[c.key].length === 0 && (
              <div className="text-[11px] text-slate-400 text-center py-6">arraste um ticket para aqui</div>
            )}
            {byCol[c.key].map((r) => (
              <div key={r.id}
                   draggable
                   onDragStart={() => setDrag(r.id)}
                   onDragEnd={() => { setDrag(null); setOver(null); }}
                   onClick={() => onOpen(r)}
                   data-testid={`kanban-card-${r.id}`}
                   className={`bg-white rounded-md border border-slate-200 border-l-4 ${PRIO_BORDER[r.prioridade] || "border-l-slate-300"} p-2.5 shadow-sm hover:shadow-md hover:border-blue/50 cursor-grab active:cursor-grabbing transition-all`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <code className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{r.referencia || "-"}</code>
                  {r.sla_excedido && <span className="text-[9px] font-bold bg-red-100 text-red-700 rounded-full px-1.5 py-0.5 uppercase tracking-wide">SLA</span>}
                  {!r.sla_excedido && !r.sla_respondido && r.sla_restante_horas != null && r.sla_restante_horas >= 0 && r.sla_restante_horas <= 4 && (
                    <span className="text-[9px] font-bold bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5">{r.sla_restante_horas}h</span>
                  )}
                </div>
                <div className="text-[13px] font-semibold text-navy leading-snug line-clamp-2">{r.assunto}</div>
                <div className="text-[11px] text-slate-500 mt-1 truncate">{r.organizacao_nome || "-"}</div>
                <div className="flex items-center justify-between mt-2">
                  <Badge value={r.prioridade} />
                  <span className="text-[10px] text-slate-400">{dt(r.criado_em)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
